import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import StudentLayout from '../../components/StudentLayout';
import PushNotificationToggle from '../../components/PushNotificationToggle';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Avatar,
  IconButton
} from '@mui/material';
import {
  School as BatchIcon,
  Payment as PaymentIcon,
  Notifications as NotificationIcon,
  Event as EventIcon,
  MenuBook as ContentIcon,
  CheckCircle as PaidIcon,
  Pending as PendingIcon,
  NotificationsActive as AlertIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import axios from 'axios';

const StudentDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [studentName, setStudentName] = useState('');
  const [dashboardData, setDashboardData] = useState({
    batch: null,
    payments: { total_paid: 0, total_pending: 0 },
    content_available: 0,
    unread_notifications: 0,
    upcoming_events: 0
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/student/dashboard', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data);
      
      // Get student name from profile
      try {
        const profileRes = await axios.get('/api/student/profile', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStudentName(profileRes.data.student?.student_name || '');
      } catch (e) {
        console.log('Could not fetch profile');
      }
      
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Error loading dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <StudentLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </StudentLayout>
    );
  }

  return (
    <StudentLayout>
      <Box sx={{ width: '100%', overflow: 'hidden', pb: 2 }}>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Welcome Header Card - Compact */}
        <Card sx={{ 
          mb: 2,
          background: 'linear-gradient(135deg, #D4AF37 0%, #B8941F 100%)',
          color: 'white',
          borderRadius: 3,
          boxShadow: '0 4px 12px rgba(212, 175, 55, 0.3)',
        }}>
          <CardContent sx={{ py: 2, px: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  width: 44, 
                  height: 44,
                  fontSize: '1.2rem',
                  fontWeight: 700
                }}>
                  {studentName ? studentName.charAt(0).toUpperCase() : 'S'}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                    Hi, {studentName ? studentName.split(' ')[0] : 'there'}! 👋
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Chinmudra Academy • Student Portal
                  </Typography>
                </Box>
              </Box>
              <IconButton 
                sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)' }}
                onClick={() => navigate('/student/notifications')}
              >
                <NotificationIcon />
              </IconButton>
            </Box>
          </CardContent>
        </Card>

        {/* Batch Info - Compact */}
        {dashboardData.batch && (
          <Card sx={{ 
            mb: 2,
            borderRadius: 3,
            border: '2px solid rgba(212, 175, 55, 0.3)',
            bgcolor: '#FFFBF0'
          }}>
            <CardContent sx={{ py: 1.5, px: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ bgcolor: '#E3F2FD', width: 40, height: 40 }}>
                  <BatchIcon sx={{ color: '#1976D2' }} />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#2C2416' }}>
                    {dashboardData.batch.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {dashboardData.batch.description || 'Your current batch'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* YOUR OVERVIEW - Horizontal scrollable on mobile */}
        <Typography variant="overline" sx={{ color: '#666', fontWeight: 700, letterSpacing: 1, mb: 1, display: 'block' }}>
          YOUR OVERVIEW
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          gap: 1.5, 
          mb: 2,
          overflowX: 'auto',
          pb: 1,
          mx: -1,
          px: 1,
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
        }}>
          {/* Paid */}
          <Card sx={{ 
            minWidth: isMobile ? 100 : 120,
            flex: isMobile ? '0 0 auto' : 1,
            borderRadius: 3,
            border: '1px solid #E8F5E9',
            bgcolor: '#F1F8E9',
            textAlign: 'center'
          }}>
            <CardContent sx={{ py: 1.5, px: 1.5 }}>
              <Avatar sx={{ bgcolor: '#C8E6C9', width: 36, height: 36, mx: 'auto', mb: 0.5 }}>
                <PaidIcon sx={{ color: '#4CAF50', fontSize: 20 }} />
              </Avatar>
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#4CAF50' }}>
                ₹{Math.round(dashboardData.payments.total_paid)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>Paid</Typography>
            </CardContent>
          </Card>

          {/* Pending */}
          <Card sx={{ 
            minWidth: isMobile ? 100 : 120,
            flex: isMobile ? '0 0 auto' : 1,
            borderRadius: 3,
            border: '1px solid #FFF3E0',
            bgcolor: '#FFF8E1',
            textAlign: 'center'
          }}>
            <CardContent sx={{ py: 1.5, px: 1.5 }}>
              <Avatar sx={{ bgcolor: '#FFE0B2', width: 36, height: 36, mx: 'auto', mb: 0.5 }}>
                <PendingIcon sx={{ color: '#FF9800', fontSize: 20 }} />
              </Avatar>
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#FF9800' }}>
                ₹{Math.round(dashboardData.payments.total_pending)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>Pending</Typography>
            </CardContent>
          </Card>

          {/* Materials */}
          <Card sx={{ 
            minWidth: isMobile ? 100 : 120,
            flex: isMobile ? '0 0 auto' : 1,
            borderRadius: 3,
            border: '1px solid #E3F2FD',
            bgcolor: '#E8F4FD',
            textAlign: 'center'
          }}>
            <CardContent sx={{ py: 1.5, px: 1.5 }}>
              <Avatar sx={{ bgcolor: '#BBDEFB', width: 36, height: 36, mx: 'auto', mb: 0.5 }}>
                <ContentIcon sx={{ color: '#2196F3', fontSize: 20 }} />
              </Avatar>
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#2196F3' }}>
                {dashboardData.content_available}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>Materials</Typography>
            </CardContent>
          </Card>

          {/* Alerts/Notifications */}
          <Card sx={{ 
            minWidth: isMobile ? 100 : 120,
            flex: isMobile ? '0 0 auto' : 1,
            borderRadius: 3,
            border: '1px solid #F3E5F5',
            bgcolor: '#FCE4EC',
            textAlign: 'center'
          }}>
            <CardContent sx={{ py: 1.5, px: 1.5 }}>
              <Avatar sx={{ bgcolor: '#E1BEE7', width: 36, height: 36, mx: 'auto', mb: 0.5 }}>
                <AlertIcon sx={{ color: '#9C27B0', fontSize: 20 }} />
              </Avatar>
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#9C27B0' }}>
                {dashboardData.unread_notifications}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>Alerts</Typography>
            </CardContent>
          </Card>
        </Box>

        {/* QUICK ACCESS - 2x2 Grid */}
        <Typography variant="overline" sx={{ color: '#666', fontWeight: 700, letterSpacing: 1, mb: 1, display: 'block' }}>
          QUICK ACCESS
        </Typography>
        <Grid container spacing={1.5}>
          {/* Learning Content */}
          <Grid item xs={6}>
            <Card
              onClick={() => navigate('/student/content')}
              sx={{
                cursor: 'pointer',
                borderRadius: 3,
                border: '1px solid rgba(212, 175, 55, 0.2)',
                bgcolor: '#FFFBF0',
                transition: 'all 0.2s ease',
                '&:hover': { 
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(212, 175, 55, 0.2)'
                },
              }}
            >
              <CardContent sx={{ py: 2, px: 2 }}>
                <Avatar sx={{ bgcolor: '#E3F2FD', width: 44, height: 44, mb: 1 }}>
                  <ContentIcon sx={{ color: '#1976D2' }} />
                </Avatar>
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#2C2416', mb: 0.25 }}>
                  Learning Content
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', color: '#1976D2' }}>
                  <Typography variant="caption" fontWeight={600}>Open</Typography>
                  <ChevronRightIcon sx={{ fontSize: 16 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Notifications */}
          <Grid item xs={6}>
            <Card
              onClick={() => navigate('/student/notifications')}
              sx={{
                cursor: 'pointer',
                borderRadius: 3,
                border: '1px solid rgba(212, 175, 55, 0.2)',
                bgcolor: '#FFFBF0',
                transition: 'all 0.2s ease',
                '&:hover': { 
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(212, 175, 55, 0.2)'
                },
              }}
            >
              <CardContent sx={{ py: 2, px: 2 }}>
                <Avatar sx={{ bgcolor: '#F3E5F5', width: 44, height: 44, mb: 1 }}>
                  <NotificationIcon sx={{ color: '#7B1FA2' }} />
                </Avatar>
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#2C2416', mb: 0.25 }}>
                  Notifications
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', color: '#7B1FA2' }}>
                  <Typography variant="caption" fontWeight={600}>Open</Typography>
                  <ChevronRightIcon sx={{ fontSize: 16 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Events */}
          <Grid item xs={6}>
            <Card
              onClick={() => navigate('/student/events')}
              sx={{
                cursor: 'pointer',
                borderRadius: 3,
                border: '1px solid rgba(212, 175, 55, 0.2)',
                bgcolor: '#FFFBF0',
                transition: 'all 0.2s ease',
                '&:hover': { 
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(212, 175, 55, 0.2)'
                },
              }}
            >
              <CardContent sx={{ py: 2, px: 2 }}>
                <Avatar sx={{ bgcolor: '#FFEBEE', width: 44, height: 44, mb: 1 }}>
                  <EventIcon sx={{ color: '#C62828' }} />
                </Avatar>
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#2C2416', mb: 0.25 }}>
                  Events
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', color: '#C62828' }}>
                  <Typography variant="caption" fontWeight={600}>Open</Typography>
                  <ChevronRightIcon sx={{ fontSize: 16 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Payments */}
          <Grid item xs={6}>
            <Card
              onClick={() => navigate('/student/payments')}
              sx={{
                cursor: 'pointer',
                borderRadius: 3,
                border: '1px solid rgba(212, 175, 55, 0.2)',
                bgcolor: '#FFFBF0',
                transition: 'all 0.2s ease',
                '&:hover': { 
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(212, 175, 55, 0.2)'
                },
              }}
            >
              <CardContent sx={{ py: 2, px: 2 }}>
                <Avatar sx={{ bgcolor: '#FFF3E0', width: 44, height: 44, mb: 1 }}>
                  <PaymentIcon sx={{ color: '#EF6C00' }} />
                </Avatar>
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#2C2416', mb: 0.25 }}>
                  Payments
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', color: '#EF6C00' }}>
                  <Typography variant="caption" fontWeight={600}>Open</Typography>
                  <ChevronRightIcon sx={{ fontSize: 16 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Push Notification Toggle - Compact at bottom */}
        <Box sx={{ mt: 2 }}>
          <PushNotificationToggle />
        </Box>
      </Box>
    </StudentLayout>
  );
};

export default StudentDashboard;
